// Entry point. The customer's <script async src="…/loader.js"> runs this:
// it paints the bubble right away, fetches the project's widget config in
// the background, and builds the panel on first open.
//
// Options come from the <script> tag:
//   data-client-id="client_…"        required
//   data-user-jwt="…"                optional, identity verification
//   data-api-base="https://…"        optional override
//   data-position="bottom_left"      optional, default from the project
//   data-primary-color="#…"          optional
//   data-accent-color="#…"           optional
//   data-greeting="…"                optional
//   data-project-name="…"            optional, panel title
//   data-require-identity="true"     optional; hide until a JWT verifies.
//                                    Absent: the project's setting decides.
//   data-launcher="hidden"           optional; no floating bubble. The page
//                                    opens the chat with window.airys.open().
//   data-preview="true"              admin Live Preview iframe only
//
// The stable global lives at `window.airys` (WidgetGlobal in ./types).

import { createApi } from './api'
import { readableForeground, shades, softOn } from './color'
import { parsePublicConfig, readOptions, resolveTheme } from './config'
import { createEmitter } from './events'
import { ensureCaprasimo } from './fonts'
import { ICON_BUBBLE } from './icons'
import { buildBubble } from './panel'
import { installPreviewChannel, postReady } from './preview'
import { createSessionManager, IdentityError } from './session'
import { configureStorage } from './storage'
import { STYLES } from './styles'
import type { Position, PublicConfig, WidgetEvent, WidgetGlobal } from './types'
import { mount, type MountHandle } from './widget'

// Injected by Vite at build time; 'dev' in source.
declare const __AIRYS_VERSION__: string
const VERSION: string = typeof __AIRYS_VERSION__ === 'string' ? __AIRYS_VERSION__ : 'dev'

type LoadedWindow = Window & {
  __airysWidgetLoaded?: boolean
  airys?: WidgetGlobal
}

;(function boot() {
  const w = window as unknown as LoadedWindow
  // Idempotent: a page that includes loader.js twice gets one widget.
  // shutdown() clears the flag so a fresh re-injection mounts cleanly.
  if (w.__airysWidgetLoaded) return
  w.__airysWidgetLoaded = true

  const script = currentScript()
  if (!script) return
  const opts = readOptions(script)
  if (!opts.preview && !opts.clientId) {
    console.warn('[airys widget %s] data-client-id is required on the loader script tag', VERSION)
    return
  }

  const emitter = createEmitter()
  const host = document.createElement('airys-widget-host')
  const root = host.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = STYLES
  root.appendChild(style)

  // Each customer color brings its own readable text color along, and the
  // accent also supplies the washes, rings and link colour the panel is
  // trimmed with — otherwise the chat would wear our colours on their site.
  function applyColors(primary?: string, accent?: string) {
    if (primary) {
      host.style.setProperty('--airys-primary', primary)
      host.style.setProperty('--airys-primary-fg', readableForeground(primary))
      const soft = softOn(primary)
      if (soft) host.style.setProperty('--airys-primary-soft', soft)
    }
    if (accent) {
      host.style.setProperty('--airys-accent', accent)
      host.style.setProperty('--airys-accent-fg', readableForeground(accent))
      const scale = shades(accent)
      if (scale) {
        host.style.setProperty('--airys-trim-wash', scale.wash)
        host.style.setProperty('--airys-trim-ring', scale.ring)
        host.style.setProperty('--airys-trim', scale.base)
        host.style.setProperty('--airys-trim-text', scale.text)
      }
    }
  }
  function applyPosition(position: Position) {
    host.setAttribute('data-position', position)
  }
  applyColors(opts.primaryColor, opts.accentColor)
  applyPosition(opts.position ?? 'bottom_right')
  if (opts.preview) host.setAttribute('data-preview', 'true')

  // `data-launcher="hidden"`: the page opens the chat from its own control,
  // so the widget paints no button. Built either way so nothing downstream
  // has to check, and simply never attached.
  const launcherHidden = opts.launcher === 'hidden'
  const bubble = buildBubble(ICON_BUBBLE)
  if (!launcherHidden) root.appendChild(bubble)
  document.body.appendChild(host)
  ensureCaprasimo(opts.assetBase)

  // Gating: keep the host in the DOM (so the API stays reachable) but
  // invisible until identify(jwt) succeeds. Only `verified` hides the
  // bubble — `ask` lets the panel open and collects a name and an email
  // there instead. The page decides with data-require-identity; when it
  // says nothing, the project's setting does, once the config lands.
  let requireIdentity = opts.requireIdentity ?? false
  let asksVisitor = false
  let identified = false
  if (requireIdentity && !opts.preview) host.style.display = 'none'

  configureStorage(opts.clientId)
  const api = createApi(opts.apiBase)
  const session = createSessionManager(api, {
    clientId: opts.clientId,
    getJwt: () => opts.userJwt,
    asksVisitor: () => asksVisitor,
  })

  // ── Preview mode: admin iframe, no network ──────────────────────────
  if (opts.preview) {
    const theme = resolveTheme(opts, null)
    const handle = mount(root, opts, theme, { api, session }, {
      onClose: () => { bubble.style.display = '' },
      onResolved: () => {},
      onIdentityExpired: () => {},
    })
    bubble.style.display = 'none'
    handle.open()
    bubble.addEventListener('click', () => {
      bubble.style.display = 'none'
      handle.open()
    })
    installPreviewChannel((u) => {
      applyColors(u.primaryColor, u.accentColor)
      if (u.position) applyPosition(u.position)
      handle.setTheme({
        greeting: u.greeting === undefined ? undefined : u.greeting || resolveTheme(opts, null).greeting,
        title: u.projectName === undefined ? undefined : u.projectName || resolveTheme(opts, null).title,
        logoUrl: u.logoUrl,
      })
    })
    postReady()
    return
  }

  // ── Config: fetched while the bubble is already on screen ───────────
  const configPromise: Promise<PublicConfig | null> = api.getConfig(opts.clientId).then(
    (raw) => {
      const cfg = parsePublicConfig(raw)
      if (!opts.primaryColor || !opts.accentColor) {
        applyColors(opts.primaryColor ?? cfg.primaryColor, opts.accentColor ?? cfg.accentColor)
      }
      if (!opts.position && cfg.position) applyPosition(cfg.position)
      // data-require-identity="false" opts a page out of both modes.
      if (opts.requireIdentity !== false) asksVisitor = cfg.visitorIdentity === 'ask'
      if (opts.requireIdentity === undefined && cfg.visitorIdentity === 'verified') {
        requireIdentity = true
        // The bubble was painted before the config arrived, so it may need
        // taking back until a JWT verifies.
        if (!identified) host.style.display = 'none'
        if (opts.userJwt) void performIdentify(opts.userJwt)
      }
      return cfg
    },
    (err) => {
      console.warn('[airys widget %s] config fetch failed:', VERSION, err)
      return null
    },
  )

  // ── Panel: built on first open ──────────────────────────────────────
  let handle: MountHandle | null = null
  let opening: Promise<void> | null = null
  let down = false

  function setBubbleOpen(open: boolean) {
    if (launcherHidden) return
    bubble.style.display = open ? 'none' : ''
    bubble.setAttribute('aria-expanded', String(open))
  }

  async function ensureOpen(): Promise<void> {
    if (down) return
    if (opening) return opening
    setBubbleOpen(true)
    opening = (async () => {
      const cfg = await configPromise
      if (down) return
      if (!handle) {
        handle = mount(root, opts, resolveTheme(opts, cfg), { api, session }, {
          onClose: () => {
            setBubbleOpen(false)
            // Focus goes back to the button that opened it. With no button,
            // the page owns where focus lands — moving it would steal it from
            // whatever the page focused.
            if (!launcherHidden) bubble.focus()
            emitter.emit('closed')
          },
          onResolved: () => emitter.emit('conversation-resolved'),
          onIdentityExpired: () => emitter.emit('identity-expired'),
        })
      }
      handle.open()
      emitter.emit('opened')
    })().finally(() => {
      opening = null
    })
    return opening
  }

  bubble.addEventListener('click', () => void ensureOpen())

  // One path for boot-time (data-user-jwt) and runtime (identify()) identity.
  async function performIdentify(jwt: string | null): Promise<void> {
    opts.userJwt = jwt ?? undefined
    try {
      if (handle) await handle.identify(jwt)
      else await session.rebind(jwt)
    } catch (err) {
      identified = false
      emitter.emit(err instanceof IdentityError ? 'identity-expired' : 'identity-failed')
      if (requireIdentity) host.style.display = 'none'
      return
    }
    identified = !!jwt
    if (jwt) {
      if (requireIdentity) host.style.display = ''
      emitter.emit('identity-verified')
    } else if (requireIdentity) {
      host.style.display = 'none'
    }
  }

  // While gating, the bubble can only show once the JWT verifies, so mint
  // eagerly. Otherwise the first send carries the JWT. A project-level
  // requirement runs the same path once the config lands.
  if (opts.userJwt && requireIdentity) void performIdentify(opts.userJwt)

  // ── Public JS API ───────────────────────────────────────────────────
  w.airys = {
    version: VERSION,
    open() { void ensureOpen() },
    close() { handle?.close() },
    identify(jwt) { void performIdentify(jwt) },
    on(event, cb) { return emitter.on(event, cb) },
    off(event, cb) { emitter.off(event, cb) },
    shutdown() {
      if (down) return
      down = true
      try { handle?.destroy() } catch { /* best effort */ }
      handle = null
      host.remove()
      w.__airysWidgetLoaded = false
      emitter.clear()
      // Keep a property at window.airys so late callers get a warning
      // instead of "undefined is not an object".
      w.airys = makeShutdownStub()
    },
  }
})()

function makeShutdownStub(): WidgetGlobal {
  const warn = () => console.warn('[airys widget] widget has been shut down; re-inject the loader script to use it again')
  return {
    version: VERSION,
    open: warn,
    close: warn,
    identify: warn,
    on: (_e: WidgetEvent, _cb: () => void) => { warn(); return () => {} },
    off: warn,
    shutdown() {},
  }
}

function currentScript(): HTMLScriptElement | null {
  // document.currentScript is null inside <script type="module">, which is
  // how Vite dev serves /src/loader.ts. Fall back to the last loader src.
  if (document.currentScript instanceof HTMLScriptElement) return document.currentScript
  const scripts = Array.from(document.scripts)
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i]!
    if (s.src && /loader\.(?:js|ts|mjs)(?:\?|$)/.test(s.src)) return s
  }
  return null
}
